import { ComponentProps, ReactNode, useMemo } from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import TableBody from 'components/elements/LeadsTable/Body';
import ColumnSelector from 'components/elements/LeadsTable/ColumnSelector';
import {
  getTableDimensions,
  hasFlexColumn,
} from 'components/elements/LeadsTable/constants';
import TableHeader from 'components/elements/LeadsTable/Header';
import { useLeadsTableColumnPreferences } from 'components/elements/LeadsTable/useLeadsTableColumnPreferences';
import Table from 'components/layout/Table';
import { ScrollableContainerProps } from 'components/layout/Table/withScrollableContainer';
import { useLeadsTableNotes } from 'hooks/useLeadsTableNotes';
import { PaginatedProject } from 'types';
import { renameDocuments } from 'utils/renameDocuments';
import type { TColumnProps, TLeadContext } from 'components/elements/LeadsTable/types';

const tableStyle = {
  border: 'none',
  backgroundColor: 'transparent',
  overflowX: 'unset',
  overflowY: 'unset',
  tableLayout: 'fixed',
};

const containerStyle = {
  width: '100%',
  // Behave like the table's own scroll wrapper used to when this component was
  // its root: a flex column that can shrink (minHeight: 0) so the inner
  // scrollable container fills the available height and scrolls, instead of the
  // table growing to its full content height.
  display: 'flex',
  flexDirection: 'column' as const,
  minHeight: 0,
};

type Props = TColumnProps &
  ScrollableContainerProps & {
    items: PaginatedProject[];
    hasClickableRows?: boolean;
    leadContext?: TLeadContext;
    sx?: BoxProps['sx'];
    ariaLabel?: string;
    isLoading?: boolean;
    disableSorting?: boolean;
    header?: ReactNode;
    footer?: ReactNode;
    cardSx?: ComponentProps<typeof Table>['cardSx'];
  };

const LeadsTable = ({
  sx,
  items,
  hasClickableRows = true,
  leadContext,
  columnProps,
  ariaLabel,
  isLoading = false,
  disableSorting = false,
  ...props
}: Props) => {
  const {
    displayOrder,
    reorderColumns,
    manageableColumns,
    visibleColumns,
    setVisibleSelection,
    persistVisibleSelection,
  } = useLeadsTableColumnPreferences();
  const showStatus = columnProps?.showStatus ?? false;
  const visibleColumnOrder = useMemo(
    () => displayOrder(showStatus),
    [displayOrder, showStatus],
  );

  const manageableColumnIds = useMemo(
    () => manageableColumns(showStatus),
    [manageableColumns, showStatus],
  );

  const { notesByEntityId, isNotesLoading } = useLeadsTableNotes(
    items,
    isLoading,
    visibleColumnOrder,
  );

  const processedItems = items.map((item) => ({
    ...item,
    latest_activity: {
      ...item.latest_activity,
      type: renameDocuments(item.latest_activity?.type),
      date: item.latest_activity?.date ?? '',
      status: item.latest_activity?.status ?? '',
      description: item.latest_activity?.description ?? '',
    },
  }));

  const style = useMemo(
    () => ({ ...tableStyle, ...getTableDimensions(visibleColumnOrder), ...sx }),
    [visibleColumnOrder, sx],
  );

  // When no flexible column is visible, a trailing spacer cell absorbs the
  // leftover width so the table fills the page without stretching fixed columns.
  const showSpacerColumn = !hasFlexColumn(visibleColumnOrder);

  const handleReorderColumns = useMemo(
    () => (fromId: string, toId: string) => reorderColumns(fromId, toId, showStatus),
    [reorderColumns, showStatus],
  );

  const handleChangeVisibleColumns = useMemo(
    () => (selectedKeys: string[]) => setVisibleSelection(selectedKeys, showStatus),
    [setVisibleSelection, showStatus],
  );

  const columnSelector = (
    <ColumnSelector
      manageableColumnIds={manageableColumnIds}
      visibleColumns={visibleColumns}
      onChangeSelection={handleChangeVisibleColumns}
      onClose={persistVisibleSelection}
    />
  );

  return (
    <Box sx={containerStyle}>
      <Table
        sx={style}
        aria-label={ariaLabel}
        id="mercator-lead-table"
        settings={columnSelector}
        {...props}
      >
        <TableHeader
          columnProps={columnProps}
          visibleColumnOrder={visibleColumnOrder}
          reorderColumns={handleReorderColumns}
          disableSorting={disableSorting}
          showSpacerColumn={showSpacerColumn}
        />
        <TableBody
          items={processedItems}
          hasClickableRows={hasClickableRows}
          columnProps={columnProps}
          leadContext={leadContext}
          isLoading={isLoading}
          visibleColumnOrder={visibleColumnOrder}
          notesByEntityId={notesByEntityId}
          isNotesLoading={isNotesLoading}
          showSpacerColumn={showSpacerColumn}
        />
      </Table>
    </Box>
  );
};

export default LeadsTable;
